import 'reflect-metadata';
import { Container } from 'inversify';
import {
  DateService,
  FeatureFlags,
  FeatureFlagsStore,
  getLogger,
  logger,
  UtIdProvider,
  CurrentLang,
  IdProvider,
  GetLogger
} from '@unitedtraders/utex-site-common';
import { Observable } from 'rxjs';
// @ts-ignore
import Cookies from 'js-cookie';
import { navigate } from 'gatsby';
import { ApiClient } from '@unitedtraders/dizraptor-gateway-api-sdk';
import type { Logger, RootLogger } from 'loglevel';
import type { History, HistoryLocation } from '@reach/router';

import type { AppConfig } from '@type/AppConfig';
import { IsUserAuthorized, isUserAuthorized } from '@services/isUserAuthorized';
import { SessionService } from '@services/SessionService';
import { zendeskProvider } from '@services/zendeskProvider';
import { podsightsProvider } from '@services/podsightsProvider';
import { UserCardsStore } from '@stores/UserCardsStore';
import { GqlService } from '@services/GqlService';
import { NavigationServiceImpl } from '@services/NavigationService';
import { FirstClientMountService } from '@services/FirstClientMountService';
import { KYCStatusStore } from '@stores/KYCStatusStore';
import { EntityCache } from '@services/EntityCache';
import { IdeasStore } from '@stores/IdeasStore';
import { UserInfoDict, UserInfoStore } from '@stores/UserInfoStore';
import { createInstance as createBusInstance, EventBus } from '@services/EventBus';
import { Env, getConfig } from '../configs/getConfig';
import { iocNames } from '../iocNames';

declare global {
  interface Window {
    _ioc: unknown;
    _iocNames: unknown;
  }
}

export const createIOC = () => {
  const ioc = new Container({ skipBaseClassChecks: true });

  ioc.bind(iocNames.appConfig).toConstantValue(getConfig(process.env.GATSBY_ENV as Env));

  ioc
    .bind<FeatureFlags>(iocNames.featureFlagsStore)
    .toDynamicValue(({ container }) => {
      const localStorage = container.get<Storage>(iocNames.localStorage);
      const { featureFlags } = container.get<AppConfig>(iocNames.appConfig);
      const window = container.get<Window>(iocNames.window);

      return new FeatureFlagsStore(localStorage, featureFlags, getLogger, window);
    })
    .inSingletonScope();

  ioc.bind(iocNames.sessionService).to(SessionService).inSingletonScope();
  ioc.bind(iocNames.isUserAuthorized).toDynamicValue(isUserAuthorized);
  ioc.bind(iocNames.logger).toConstantValue(logger);
  ioc.bind(iocNames.getLogger).toFactory(() => getLogger);
  ioc.bind(iocNames.cookies).toConstantValue(Cookies);

  ioc.bind(iocNames.currentLang).toConstantValue({
    currentLanguage: 'en',
    changeLanguage: () => {},
    langObserver: new Observable()
  });

  ioc.bind(iocNames.firstClientMountService).to(FirstClientMountService).inSingletonScope();

  ioc
    .bind<IdProvider>(iocNames.gaIdProvider)
    .toDynamicValue(({ container }) => {
      const window = container.get<Window>(iocNames.window);

      return {
        getId: () => {
          // @ts-ignore
          const gaId = window.gaGlobal?.vid;
          return Promise.resolve(gaId);
        }
      };
    })
    .inSingletonScope();

  ioc
    .bind(iocNames.utIdProvider)
    .toDynamicValue(({ container }) => {
      const cookies = container.get<Cookies.CookiesStatic>(iocNames.cookies);

      return new UtIdProvider(cookies);
    })
    .inSingletonScope();

  ioc
    .bind<ApiClient>(iocNames.dizraptorApi)
    .toDynamicValue(({ container }) => {
      const { dizraptorGateway } = container.get<AppConfig>(iocNames.appConfig);
      const rootLogger = container.get<RootLogger>(iocNames.logger);
      const sessionService = container.get<SessionService>(iocNames.sessionService);

      return new ApiClient(dizraptorGateway, rootLogger, sessionService.getTokens);
    })
    .inSingletonScope();

  ioc
    .bind<DateService>(iocNames.dateService)
    .toDynamicValue(({ container }) => {
      const currentLang = container.get<CurrentLang>(iocNames.currentLang);
      return new DateService(currentLang);
    })
    .inSingletonScope();

  ioc
    .bind(iocNames.zendeskWidget)
    .toDynamicValue(({ container }) => {
      const window = container.get<Window>(iocNames.window);
      return zendeskProvider(window);
    })
    .inSingletonScope();

  ioc
    .bind(iocNames.podsightsApi)
    .toDynamicValue(({ container }) => {
      const window = container.get<Window>(iocNames.window);
      const utIdProvider = container.get<UtIdProvider>(iocNames.utIdProvider);
      return podsightsProvider(window, utIdProvider);
    })
    .inSingletonScope();

  ioc
    .bind(iocNames.gqlService)
    .toDynamicValue(({ container }) => {
      const config = container.get<AppConfig>(iocNames.appConfig);
      const sessionService = container.get<SessionService>(iocNames.sessionService);
      const loggerFactory = container.get<GetLogger>(iocNames.getLogger);

      return new GqlService(config, sessionService, loggerFactory);
    })
    .inSingletonScope();

  ioc
    .bind(iocNames.userCardsStore)
    .toDynamicValue(({ container }) => {
      const gqlService = container.get<GqlService>(iocNames.gqlService);
      const entityCache = container.get<EntityCache>(iocNames.entityCache);
      const eventBus = container.get<EventBus>(iocNames.eventBus);

      return new UserCardsStore(gqlService, entityCache, eventBus);
    })
    .inSingletonScope();

  ioc
    .bind(iocNames.ideasStore)
    .toDynamicValue(({ container }) => {
      const gqlService = container.get<GqlService>(iocNames.gqlService);
      const entityCache = container.get<EntityCache>(iocNames.entityCache);
      const getIsUserAuthorized = container.get<IsUserAuthorized>(iocNames.isUserAuthorized);
      const eventBus = container.get<EventBus>(iocNames.eventBus);

      return new IdeasStore(gqlService, entityCache, getIsUserAuthorized, eventBus);
    })
    .inSingletonScope();

  ioc
    .bind(iocNames.navigationService)
    .toDynamicValue(({ container }) => {
      const location = container.get<HistoryLocation>(iocNames.location);
      const window = container.get<Window>(iocNames.window);
      const history = container.get<History>(iocNames.history);

      return new NavigationServiceImpl(location, window, history, navigate);
    })
    .inSingletonScope();

  ioc
    .bind(iocNames.kycStatus)
    .toDynamicValue(({ container }) => {
      const gqlService = container.get<GqlService>(iocNames.gqlService);
      const l = container.get<(name: string) => Logger>(iocNames.getLogger)('KYCStatusStore');

      return new KYCStatusStore(gqlService, l);
    })
    .inSingletonScope();

  ioc
    .bind(iocNames.entityCache)
    .toDynamicValue(({ container }) => {
      return new EntityCache(name => container.get(name));
    })
    .inSingletonScope();

  ioc
    .bind(iocNames.eventBus)
    .toDynamicValue(() => {
      return createBusInstance();
    })
    .inSingletonScope();

  ioc
    .bind<UserInfoDict>(iocNames.userInfoStore)
    .toDynamicValue(({ container }) => {
      const gqlService = container.get<GqlService>(iocNames.gqlService);
      const getIsUserAuthorized = container.get<IsUserAuthorized>(iocNames.isUserAuthorized);

      return new UserInfoStore(gqlService, getIsUserAuthorized);
    })
    .inSingletonScope();

  return ioc;
};
